import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TrendingUp, LayoutDashboard, LineChart, Landmark, ArrowLeftRight, FileText, Users, LogOut, Menu, X } from "lucide-react";
import { toast } from "sonner";

const clientNav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/performance", label: "Performance", icon: LineChart },
  { to: "/funds", label: "Funds", icon: Landmark },
  { to: "/cashflow", label: "Cashflow", icon: ArrowLeftRight },
  { to: "/documents", label: "Documents", icon: FileText },
];
const adminNav = [{ to: "/admin", label: "Clients", icon: Users }];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "admin" ? adminNav : clientNav;

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const NavItems = ({ onClick }) => (
    <>
      {nav.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} end onClick={onClick} data-testid={`nav-${label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold transition-colors duration-200 ${
              isActive ? "bg-[#15803d] text-white shadow-sm" : "text-slate-600 hover:bg-emerald-50 hover:text-[#15803d]"
            }`}>
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} /> {label}
        </NavLink>
      ))}
    </>
  );

  const Brand = () => (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-2xl bg-[#15803d] flex items-center justify-center text-white shadow-sm">
        <TrendingUp className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <span className="font-serif text-xl font-bold text-slate-800">Futions</span>
    </div>
  );

  const UserCard = () => (
    <div className="flex items-center gap-3 rounded-2xl bg-emerald-50/60 border border-emerald-100 p-3">
      <Avatar className="w-9 h-9 border border-emerald-200">
        {user?.picture && <AvatarImage src={user.picture} />}
        <AvatarFallback className="bg-[#15803d] text-white text-xs">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-slate-800 leading-tight truncate">{user?.name}</p>
        <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
      </div>
      <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={doLogout}
        className="text-slate-400 hover:text-[#dc2626] hover:bg-red-50 rounded-xl transition-colors duration-200">
        <LogOut className="w-[18px] h-[18px]" strokeWidth={1.75} />
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col justify-between bg-white border-r border-slate-200/80 p-5">
        <div className="space-y-8">
          <Brand />
          <nav className="space-y-1.5">
            <p className="overline px-4 mb-2">Menu</p>
            <NavItems />
          </nav>
        </div>
        <UserCard />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 h-16 flex items-center justify-between">
        <Brand />
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors duration-200">
          {open ? <X className="w-5 h-5" strokeWidth={1.75} /> : <Menu className="w-5 h-5" strokeWidth={1.75} />}
        </button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setOpen(false)}>
          <div className="absolute top-16 left-0 right-0 bg-white border-b border-slate-200 p-4 space-y-2 fade-up" onClick={(e) => e.stopPropagation()}>
            <NavItems onClick={() => setOpen(false)} />
            <div className="pt-2"><UserCard /></div>
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
